import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Phone,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import useJsonLd from "@/hooks/useJsonLd";
import { getAreaBySlug } from "@/data/serviceAreas";
import { SITE, SERVICES } from "@/data/site";

const ORIGIN =
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "https://genesisappliancerepair.com";

const ServiceArea = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const area = getAreaBySlug(slug);

  // Hooks must be unconditional — pre-compute schema payloads with fallbacks.
  const localBusinessSchema = area
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType:
          lang === "es"
            ? "Reparación de electrodomésticos"
            : "Appliance Repair",
        provider: {
          "@type": "Organization",
          name: "Genesis Appliance Repair",
          telephone: SITE.phone,
          url: `${ORIGIN}/`,
        },
        areaServed: {
          "@type": area.type === "county" ? "AdministrativeArea" : "City",
          name: area.name.en,
          geo: {
            "@type": "GeoCoordinates",
            latitude: area.coords.lat,
            longitude: area.coords.lng,
          },
          containedInPlace: {
            "@type": "AdministrativeArea",
            name: "Hawaii County, Hawaii, USA",
          },
        },
        description: area.seo.en.description,
        url: `${ORIGIN}/service-areas/${area.slug}`,
      }
    : null;

  const breadcrumbSchema = area
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: t("nav.home"),
            item: `${ORIGIN}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: lang === "es" ? "Áreas de servicio" : "Service areas",
            item: `${ORIGIN}/service-areas/hawaii-county`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: area.name[lang],
            item: `${ORIGIN}/service-areas/${area.slug}`,
          },
        ],
      }
    : null;

  useJsonLd(area ? `area-service-${slug}-${lang}` : null, localBusinessSchema);
  useJsonLd(area ? `area-crumb-${slug}-${lang}` : null, breadcrumbSchema);

  if (!area) return <Navigate to="/service-areas/hawaii-county" replace />;

  const hero = area.hero[lang];
  const insight = area.insight[lang];
  const body = area.bodyParagraphs[lang];

  return (
    <div data-testid={`page-area-${slug}`}>
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-16 sm:pt-20 pb-16">
        <Link
          to="/"
          data-testid="area-back"
          className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-[#082133] font-semibold mb-7 hover:gap-3 transition-all"
        >
          <ArrowRight className="h-3.5 w-3.5 rotate-180" />
          {t("nav.home")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
              {hero.eyebrow}
            </span>
            <h1 className="mt-4 font-display font-semibold text-[#111111] text-[40px] sm:text-[54px] lg:text-[64px] leading-[1.04] tracking-[-0.03em]">
              {hero.title}
            </h1>
            <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-[#4B5563]">
              {hero.lead}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/contact">
                <Button
                  data-testid="area-book-cta"
                  className="rounded-full h-13 px-7 bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47] text-[15px] font-medium"
                >
                  {t("common.bookOnline")}
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="area-call-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full h-12 px-6 border border-[#082133]/20 bg-white text-[#082133] hover:bg-[#082133]/5 text-[14px] font-medium"
              >
                <Phone className="h-4 w-4" />
                {SITE.phone}
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[5/4] w-full max-w-[720px] mx-auto lg:ml-auto lg:mr-0">
              <div className="absolute -top-8 -left-8 h-44 w-44 rounded-full bg-[#DCE3DE] blur-2xl opacity-70" />
              <div className="absolute -bottom-10 -right-8 h-56 w-56 rounded-full bg-[#082133]/10 blur-3xl" />
              <div className="relative h-full w-full overflow-hidden blob-mask-wide shadow-[0_45px_90px_-30px_rgba(8,33,51,0.35)]">
                <img
                  src={area.image}
                  alt={`${area.name[lang]}, Big Island of Hawaii`}
                  className="h-full w-full object-cover object-center"
                />
              </div>
              <div className="absolute -bottom-6 left-4 sm:left-8 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_24px_50px_-20px_rgba(8,33,51,0.25)] px-5 py-4 max-w-[240px]">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] font-semibold">
                  {lang === "es" ? "Atendiendo" : "Serving"}
                </div>
                <div className="mt-1.5 font-display text-[15px] text-[#111111] font-semibold flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-[#082133]" />
                  {area.shortName[lang]}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEIGHBORHOODS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-16">
        <div className="rounded-[32px] bg-[#082133] text-[#FAFAFA] px-8 sm:px-12 py-12 sm:py-14 relative overflow-hidden grain">
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-4">
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#FAFAFA]/60 font-semibold">
                {lang === "es" ? "Vecindarios cercanos" : "Nearby neighborhoods"}
              </span>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold leading-tight">
                {area.name[lang]}
              </h3>
            </div>
            <div className="md:col-span-8">
              <div className="flex flex-wrap gap-2.5">
                {area.neighborhoods.map((n) => (
                  <span
                    key={n}
                    data-testid={`area-neighborhood-${n.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="rounded-full border border-[#FAFAFA]/20 px-4 py-2 text-sm tracking-wide text-[#FAFAFA]/90"
                  >
                    {n}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSIGHT */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-16">
        <div className="rounded-3xl bg-white border border-[#E5E7EB] p-8 sm:p-12 flex flex-col sm:flex-row items-start gap-8 shadow-[0_25px_50px_-25px_rgba(8,33,51,0.18)]">
          <span className="h-14 w-14 inline-flex items-center justify-center rounded-2xl bg-[#DCE3DE] text-[#082133] flex-shrink-0">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <div className="flex-1">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
              {lang === "es" ? "Conocimiento local" : "Local insight"}
            </span>
            <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-[#111111] tracking-tight">
              {insight.title}
            </h3>
            <p className="mt-3 text-[16px] text-[#4B5563] leading-relaxed">
              {insight.body}
            </p>
          </div>
        </div>
      </section>

      {/* BODY PARAGRAPHS */}
      <section className="mx-auto max-w-3xl px-5 sm:px-8 lg:px-12 pb-20">
        <div className="space-y-6" data-testid="area-body">
          {body.map((p, i) => (
            <p
              key={i}
              className="text-[17px] leading-[1.75] text-[#1F2937]"
            >
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* SERVICES OFFERED IN THIS AREA */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-20">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#111111] tracking-[-0.02em]">
            {lang === "es"
              ? `Servicios en ${area.name[lang]}`
              : `Services in ${area.name[lang]}`}
          </h2>
          <Link
            to="/services"
            data-testid="area-view-all-services"
            className="text-[#082133] text-sm font-medium inline-flex items-center gap-2 hover:gap-3 transition-all"
          >
            {t("common.viewAllServices")}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={s.to}
              data-testid={`area-service-${s.slug}`}
              className="group rounded-2xl bg-white border border-[#E5E7EB] p-5 hover:-translate-y-1 hover:shadow-[0_18px_36px_-20px_rgba(8,33,51,0.15)] transition-all"
            >
              <div className="aspect-[5/4] rounded-xl overflow-hidden mb-4">
                <img
                  src={s.image}
                  alt={t(`serviceData.${s.key}.title`)}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="font-display text-[15px] font-semibold text-[#111111] leading-snug">
                  {t(`serviceData.${s.key}.title`)}
                </div>
                <ArrowUpRight className="h-4 w-4 text-[#082133] flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-24">
        <div className="rounded-[32px] border border-[#E5E7EB] bg-white px-8 sm:px-12 py-14 sm:py-20 shadow-[0_30px_60px_-30px_rgba(8,33,51,0.15)] relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-[#DCE3DE]/60 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 items-end gap-10">
            <div className="lg:col-span-8">
              <h3 className="font-display font-semibold text-[#111111] text-[28px] sm:text-[38px] leading-[1.1] tracking-[-0.02em]">
                {lang === "es"
                  ? `¿Necesita un técnico en ${area.shortName[lang]}?`
                  : `Need a technician in ${area.shortName[lang]}?`}
              </h3>
              <p className="mt-3 max-w-xl text-[#4B5563] text-[15px] leading-relaxed">
                {t("home.ctaBlockLead")}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
              <Link to="/contact">
                <Button
                  data-testid="area-bottom-book"
                  className="w-full rounded-full h-14 bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47]"
                >
                  {t("common.bookOnline")}
                </Button>
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="area-bottom-call"
                className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-6 border border-[#082133]/20 bg-white text-[#082133] hover:bg-[#082133]/5 text-[14px] font-medium"
              >
                <Phone className="h-4 w-4" />
                {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceArea;
